<script setup lang="ts">
import { computed, useAuth, useDialog } from '#imports';

interface Props {
  filteredSpotsCount?: number;
  totalSpotsCount?: number;
  showCounts?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  filteredSpotsCount: 0,
  totalSpotsCount: 0,
  showCounts: true,
});

const { currentUser } = useAuth();
const { openDialog, setDialogFullscreen } = useDialog();

const openPricingDialog = (tier: 'premium' | 'support') => {
  openDialog('MPPricing', {
    isOpen: 'pricingDialogIsOpen',
    showSupporterTier: false,
  });
  setDialogFullscreen(true);
};
</script>

<template>
  <article
    :class="[
      'unlock-cta',
      !currentUser ? 'public-cta' : 'authenticated-cta'
    ]"
  >
    <small v-if="showCounts">Showing {{ filteredSpotsCount }} out of {{ totalSpotsCount }} Spots</small>

    <template v-if="!currentUser">
      <strong>Join Mappr</strong>
      <p>Discover curated lists of the best spots, handpicked by experts and creators</p>
      <MPButton
        label="Sign Up"
        version="secondary"
        rounded
        size="large"
        icon-color="var(--color-rose-500)"
        background-color="var(--color-white)"
        @click="openDialog('MPAuth', { startOn: 'signup' })"
      />
    </template>

    <template v-else-if="currentUser && !currentUser.hasActiveSubscription">
      <strong>Mappr Premium</strong>
      <p>Unlock thousands more spots and exclusive recommendations by subscribing, while supporting creators at the same time</p>
      <MPButton
        label="Go Premium"
        version="primary"
        rounded
        background-color="var(--color-white)"
        icon-color="var(--color-green-600)"
        size="large"
        @click="openPricingDialog('premium')"
      />
    </template>

    <div class="bg-logo bg-logo-green">
    <svg width="159" height="187" viewBox="0 0 159 187" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fill-rule="evenodd" clip-rule="evenodd" d="M92.1364 0.0570068C91.0705 0.0570068 89.8522 0.0570069 88.7863 0.361565L43.1026 12.8484C42.1889 13.153 41.4275 13.4576 40.6661 13.9144C39.9047 14.3712 39.1433 14.9803 38.5342 15.7417C37.9251 16.5031 37.316 17.2645 36.7069 18.0259C36.25 18.7873 35.7932 19.701 35.6409 20.6147L1.37816 148.834C0.921322 150.204 0.769043 151.575 0.769043 152.945C0.769043 154.163 0.921322 155.534 1.22588 156.752C1.53044 157.818 1.98727 158.884 2.59639 159.798C3.20551 160.559 3.9669 161.32 4.88058 161.777L57.7214 185.533C59.0919 186.142 60.6147 186.446 62.1375 186.294C63.6603 186.142 65.0308 185.685 66.0967 184.771C67.315 183.858 68.3809 182.64 69.2946 181.269C70.2083 179.594 70.8174 177.919 70.9697 176.092L73.863 156.448L106.451 166.346C107.669 166.802 109.039 166.803 110.41 166.65C111.628 166.498 112.846 165.889 113.912 165.127C114.978 164.366 115.892 163.3 116.501 162.082C117.11 160.711 117.567 159.341 117.719 157.818L118.938 141.981L148.784 147.311C150.002 147.463 151.221 147.463 152.439 147.159C153.505 146.854 154.571 146.397 155.484 145.636C156.398 144.874 157.007 143.961 157.616 143.047C158.073 141.981 158.378 140.763 158.378 139.545V28.8377C158.378 28.2286 158.226 27.6195 157.769 27.1627C157.312 26.7058 156.855 26.4013 156.246 26.249C155.484 26.0967 154.875 25.9444 154.114 26.0967C153.2 26.249 152.287 26.4013 151.525 26.7058L127.313 36.2994L128.531 19.8533C128.683 19.0919 128.531 18.3305 128.074 17.7214C127.77 17.1123 127.161 16.6554 126.551 16.3509C125.79 16.0463 125.029 15.894 124.115 15.894C123.049 15.894 122.135 16.0463 121.222 16.5031L93.5069 25.7922L96.5525 5.53905C96.7048 4.62538 96.5525 3.7117 96.248 2.79803C95.9434 2.03663 95.3343 1.42752 94.7252 0.970681C93.8115 0.361565 93.0501 0.0570068 92.1364 0.0570068ZM127.313 36.1471L119.09 141.829L103.862 139.088C103.101 138.935 102.339 138.631 101.882 138.174C101.273 137.717 100.816 137.108 100.359 136.347C99.9027 135.585 99.7504 134.824 99.5981 134.063C99.4458 133.149 99.4458 132.235 99.5981 131.474L111.78 46.1975C111.933 45.5884 112.085 44.9793 112.39 44.5225C112.694 43.9134 113.151 43.3042 113.455 42.8474C113.912 42.3906 114.369 41.7814 114.978 41.4769C115.587 41.0201 116.197 40.7155 116.806 40.4109L127.313 36.1471ZM93.5069 25.7922L74.0152 156.448L57.4168 151.422C56.6554 151.118 55.894 150.661 55.2849 150.052C54.6758 149.443 54.219 148.529 53.7621 147.768C53.4576 146.854 53.153 145.788 53.153 144.874C53.0007 143.808 53.153 142.742 53.3053 141.676L75.0812 36.7562C75.2335 35.9948 75.538 35.3857 75.8426 34.6243C76.2994 33.8629 76.7563 33.2538 77.2131 32.6447C77.8222 32.0356 78.4313 31.4265 79.0404 30.9696C79.6496 30.5128 80.4109 30.2082 81.1723 29.9037L93.5069 25.7922Z" fill="black"/>
    </svg>
    </div>
  </article>
</template>

<style scoped lang="postcss">
@import '@/assets/css/typography.pcss';

.unlock-cta {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.5rem;
  padding: 1.5rem 1rem;
  border-radius: 0.5rem;
  background-color: var(--color-slate-100);
  z-index: 1;
  position: relative;

  @media (min-width: 35rem) {
    bottom: 0;
    left: 0;
    right: 0;
    text-align: left;
    justify-content: start;
    align-items: start;
    padding: 1.5rem 2rem;
  }

  small {
    @mixin typeface-small;
    line-height: 1.25rem;
    color: var(--color-slate-500);
    z-index: 1;
  }

  strong {
    @mixin typeface-summary;
    line-height: 1.75rem;
    font-weight: 700;
    color: var(--color-slate-700);
    z-index: 1;
  }

  p {
    @mixin typeface-regular;
    line-height: 1.5rem;
    color: var(--color-slate-500);
    z-index: 1;
    max-width: 28rem;
  }

  & .bg-logo {
    position: absolute;
    bottom: 0;
    right: 0;
    z-index: -1;
  }

  svg {
    path {
      fill: rgba(230, 230, 235, 0.35);
    }
  }
}

.public-cta {
  background-color: var(--color-rose-500) !important;

  small {
    color: var(--color-white);
  }

  strong {
    color: var(--color-white);
  }

  p {
    color: var(--color-white);
  }
}

.authenticated-cta {
  background-color: var(--color-green-400) !important;

  small {
    color: var(--color-white);
  }

  strong {
    color: var(--color-white);
  }

  p {
    font-size: 1rem;
    line-height: 1.5rem;
    color: var(--color-white);
  }
}
</style>
